/**
 * TenantLogViewer.tsx — Grafana Explore iframe embed for tenant container logs.
 *
 * Renders a Grafana Explore view pre-filtered to the tenant's logs via an
 * iframe embed. Requires VITE_GRAFANA_EXPLORE_URL and VITE_LOKI_DATASOURCE_UID
 * to be set in the frontend environment.
 */

interface TenantLogViewerProps {
  tenantSlug: string
}

export function TenantLogViewer({ tenantSlug }: TenantLogViewerProps) {
  const grafanaBaseUrl = import.meta.env.VITE_GRAFANA_EXPLORE_URL as string | undefined
  const lokiDatasourceUid = import.meta.env.VITE_LOKI_DATASOURCE_UID as string | undefined

  if (!grafanaBaseUrl || !lokiDatasourceUid) {
    return (
      <div style={{
        padding: '32px 24px',
        textAlign: 'center',
        color: 'var(--text-tertiary)',
        fontSize: '13px',
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        alignItems: 'center',
      }}>
        <span style={{ fontSize: '14px', fontWeight: 500, color: 'var(--text-secondary)' }}>
          Grafana not configured
        </span>
        <span>
          Set <code style={{ fontFamily: 'var(--font-mono)', fontSize: '12px' }}>VITE_GRAFANA_EXPLORE_URL</code> and{' '}
          <code style={{ fontFamily: 'var(--font-mono)', fontSize: '12px' }}>VITE_LOKI_DATASOURCE_UID</code> to enable log streaming.
        </span>
      </div>
    )
  }

  // Build the Grafana Explore URL with a pre-set LogQL query for this tenant.
  // kiosk=1 hides the Grafana header/sidebar — only the Explore panel is shown.
  const expr = `{tenant_slug="${tenantSlug}"}`
  const pane = JSON.stringify({
    datasource: lokiDatasourceUid,
    queries: [{ refId: 'A', expr, queryType: 'range' }],
    range: { from: 'now-1h', to: 'now' },
  })
  const iframeUrl = `${grafanaBaseUrl}/explore?orgId=1&left=${encodeURIComponent(pane)}&kiosk=1`

  return (
    <div className="tenant-log-viewer" style={{ width: '100%' }}>
      <iframe
        src={iframeUrl}
        title={`Logs for ${tenantSlug}`}
        style={{ width: '100%', height: '600px', border: 'none', borderRadius: 'var(--radius-md)' }}
        sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
      />
    </div>
  )
}
